import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Select,
  Typography,
  Dropdown,
  Popconfirm,
  Spin,
  Alert,
  message,
} from 'antd';
import {
  MoreOutlined,
  InboxOutlined,
  DeleteOutlined,
  SyncOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useAutoSave } from '../hooks/useAutoSave';
import { useNotebooks } from '../hooks/useNotes';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useNoteEvents } from '../hooks/useNoteEvents';
import { getNote, updateNote, deleteNote, archiveNote, unarchiveNote } from '../services/api';
import NoteEssayLinker from '../components/NoteEssayLinker';
import NoteMetadataPanel from '../components/NoteMetadataPanel';

import MilkdownEditor from '../components/MilkdownEditor';

const { Text } = Typography;

export default function NoteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const noteId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { notebooks } = useNotebooks();
  const isMobile = !useMediaQuery('(min-width: 768px)');

  const { data: note, isLoading, error } = useQuery({
    queryKey: ['note', noteId],
    queryFn: () => getNote(noteId),
    enabled: !!noteId && !isNaN(noteId),
  });

  const [title, setTitle] = useState<string | null>(null);
  const displayTitle = title ?? note?.title ?? '';

  const { handleChange, handleTitleChange, saveNow, saveStatus, isDirty, lastSavedAtRef } = useAutoSave({
    noteId,
  });

  // Track external updates via SSE
  const [externalUpdate, setExternalUpdate] = useState(false);
  const [editorRevision, setEditorRevision] = useState(0);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const handleExternalUpdate = useCallback((updatedNoteId: number) => {
    if (updatedNoteId !== noteId) return;
    // Skip SSE events that echo back our own recent save (within 2s).
    // Without this, the editor remounts and destroys the contentEditable DOM,
    // which causes iOS to dismiss the keyboard and microphone during dictation.
    const timeSinceOwnSave = Date.now() - lastSavedAtRef.current;
    if (timeSinceOwnSave < 2000) return;
    if (isDirtyRef.current) {
      // User has unsaved changes — show notification banner
      setExternalUpdate(true);
    } else {
      // No unsaved changes — silently reload editor
      setEditorRevision(r => r + 1);
      setTitle(null); // Reset to use fresh data from query
    }
  }, [noteId, lastSavedAtRef]);

  useNoteEvents({ onExternalUpdate: handleExternalUpdate });

  const handleReloadExternal = useCallback(() => {
    setExternalUpdate(false);
    setTitle(null);
    setEditorRevision(r => r + 1);
    queryClient.invalidateQueries({ queryKey: ['note', noteId] });
  }, [noteId, queryClient]);

  const handleDismissExternal = useCallback(() => {
    setExternalUpdate(false);
  }, []);

  const onTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setTitle(val);
      handleTitleChange(val);
    },
    [handleTitleChange]
  );

  const onContentChange = useCallback(
    (markdown: string) => {
      handleChange(markdown);
    },
    [handleChange]
  );

  // Keyboard shortcuts: Cmd+S to save, Escape to go back
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveNow();
      }
      if (e.key === 'Escape' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        saveNow();
        navigate('/reviewer/notes');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveNow, navigate]);

  async function handleNotebookChange(notebookId: number) {
    try {
      await updateNote(noteId, { notebookId });
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    } catch {
      message.error('Failed to move note');
    }
  }

  async function handleArchive() {
    try {
      if (note?.archivedAt) {
        await unarchiveNote(noteId);
        message.success('Note unarchived');
      } else {
        await archiveNote(noteId);
        message.success('Note archived');
      }
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    } catch {
      message.error('Failed to update note');
    }
  }

  async function handleDelete() {
    try {
      await deleteNote(noteId);
      message.success('Note deleted');
      navigate('/reviewer/notes');
    } catch {
      message.error('Failed to delete note');
    }
  }

  if (user?.role !== 'author') {
    return <Alert type="error" message="Access denied. Author only." showIcon />;
  }

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  }

  if (error || !note) {
    return <Alert type="error" message="Note not found" showIcon />;
  }

  const statusColor = saveStatus === 'error' ? 'var(--color-danger)' : saveStatus === 'saving' ? 'var(--text-secondary)' : saveStatus === 'saved' ? 'var(--color-success)' : 'var(--text-muted)';
  const statusText = saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error' : '';

  const moreMenuItems = [
    {
      key: 'archive',
      icon: <InboxOutlined />,
      label: note.archivedAt ? 'Unarchive' : 'Archive',
      onClick: handleArchive,
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: (
        <Popconfirm
          title="Delete this note?"
          onConfirm={handleDelete}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <span style={{ color: '#ff4d4f' }}>Delete</span>
        </Popconfirm>
      ),
      danger: true,
    },
  ];

  return (
    <div>
      {/* External update banner */}
      {externalUpdate && (
        <Alert
          type="info"
          message="This note was updated externally"
          action={
            <span style={{ display: 'flex', gap: 8 }}>
              <Button size="small" type="primary" icon={<SyncOutlined />} onClick={handleReloadExternal}>
                Reload
              </Button>
              <Button size="small" onClick={handleDismissExternal}>
                Keep editing
              </Button>
            </span>
          }
          style={{ marginBottom: 8 }}
          closable={false}
        />
      )}

      {/* Toolbar */}
      <div
        style={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          marginBottom: 16,
          background: 'var(--bg-base)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Select
          value={note.notebookId}
          onChange={handleNotebookChange}
          style={{ minWidth: 140 }}
          size="small"
          options={notebooks.map((nb) => ({ value: nb.id, label: nb.name }))}
        />

        <div style={{ flex: 1 }} />

        {statusText && (
          <Text style={{ color: statusColor, fontSize: 12 }}>{statusText}</Text>
        )}

        {isMobile && (
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/reviewer/notes')} style={{ color: 'var(--accent-gold)' }} />
        )}

        <Dropdown menu={{ items: moreMenuItems }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} style={{ color: '#ccc' }} />
        </Dropdown>
      </div>

      {/* Title */}
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <input
          value={displayTitle}
          onChange={onTitleChange}
          placeholder="Note title"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#f5f5f5',
            fontSize: '1.8rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            padding: '8px 0',
            marginBottom: 16,
          }}
        />

        {/* Editor */}
        <MilkdownEditor
          key={`${noteId}-${editorRevision}`}
          initialContent={note.content}
          onChange={onContentChange}
          autoFocus
          showToolbar
        />

        {/* Essay linking */}
        <NoteEssayLinker noteId={noteId} linkedEssays={note.linkedEssays} />

        <NoteMetadataPanel
          noteId={noteId}
          autoTagsRaw={note.autoTagsRaw}
          autoSummary={note.autoSummary}
        />
      </div>
    </div>
  );
}
